import { Typography } from "antd";
import { FormattedMessage } from "react-intl";
import { PermissionScopeTypes } from "~pages/Users/enums/permissionScopeTypes.ts";
import type { UserPermissionTransformed } from "~pages/Users/types";
import type { PermissionScopeResources } from "~types/permissionScope";
import type { ScopeOption } from "../types/scopeOption";

const extractScopeData = (userPermission: UserPermissionTransformed, resources: PermissionScopeResources): ScopeOption[] => {
  const {
    leagues: leaguesData = {},
    competitions: competitionsData = {},
    seasons: seasonsData = {},
    entityGroups: entityGroupsData = {},
    entities: entitiesData = {},
  } = resources;

  if (!userPermission.scope || !userPermission.scopeIds) {
    return [];
  }

  if (userPermission.scope === PermissionScopeTypes.League) {
    return userPermission.scopeIds.reduce<ScopeOption[]>((acc, currentValue) => {
      if (!currentValue) {
        return acc;
      }

      const league = leaguesData[currentValue];
      if (!league) {
        return acc;
      }

      acc.push({
        value: league.leagueId,
        label: (
          <Typography.Title className="permissions-scope-view-item-label" level={4}>
            {league.nameLocal}
          </Typography.Title>
        ),
        sortValue: league.nameLocal,
        data: [{ label: <FormattedMessage id="regionType" />, value: league?.regionType || "" }],
      });

      return acc;
    }, []);
  }

  if (userPermission.scope === PermissionScopeTypes.Competition) {
    return userPermission.scopeIds.reduce<ScopeOption[]>((acc, currentValue) => {
      if (!currentValue) {
        return acc;
      }

      const competition = competitionsData[currentValue];
      if (!competition) {
        return acc;
      }

      const league = competition.leagueId ? leaguesData[competition.leagueId] : null;

      acc.push({
        value: competition.competitionId,
        label: (
          <div>
            <Typography.Title className="permissions-scope-view-item-label" level={4}>
              {competition.nameLocal}
            </Typography.Title>
            {league && (
              <Typography.Text className="permissions-scope-view-item-label-subtitle">
                <FormattedMessage id="league" />
                {":  "}
                {league.nameLocal}
              </Typography.Text>
            )}
          </div>
        ),
        sortValue: competition.nameLocal,
        data: [{ label: <FormattedMessage id="ageGroup" />, value: competition?.ageGroup || "" }],
      });

      return acc;
    }, []);
  }

  if (userPermission.scope === PermissionScopeTypes.Season) {
    return userPermission.scopeIds.reduce<ScopeOption[]>((acc, currentValue) => {
      if (!currentValue) {
        return acc;
      }

      const season = seasonsData[currentValue];
      if (!season) {
        return acc;
      }

      const competition = competitionsData[season.competitionId];
      if (!competition) {
        return acc;
      }

      const league = competition.leagueId ? leaguesData[competition.leagueId] : null;

      acc.push({
        value: season.seasonId,
        label: (
          <div>
            <Typography.Text className="permissions-scope-view-item-label">{season.nameLocal}</Typography.Text>
            <Typography.Text className="permissions-scope-view-item-label-subtitle">
              <FormattedMessage id="competition" />
              {":  "}
              {competition.nameLocal}
            </Typography.Text>
            {league && (
              <Typography.Text className="permissions-scope-view-item-label-subtitle" style={{ display: "block", marginTop: "-4px" }}>
                <FormattedMessage id="league" />
                {":  "}
                {league.nameLocal}
              </Typography.Text>
            )}
          </div>
        ),
        sortValue: season.nameLocal,
        data: [
          { label: <FormattedMessage id="gender" />, value: season.gender },
          { label: <FormattedMessage id="ageGroup" />, value: season.ageGroup },
          { label: <FormattedMessage id="discipline" />, value: season.discipline },
          { label: <FormattedMessage id="status" />, value: season.status },
        ],
      });

      return acc;
    }, []);
  }

  if (userPermission.scope === PermissionScopeTypes.EntityGroup) {
    return userPermission.scopeIds.reduce<ScopeOption[]>((acc, currentValue) => {
      if (!currentValue) {
        return acc;
      }

      const entityGroup = entityGroupsData[currentValue];
      if (!entityGroup) {
        return acc;
      }

      acc.push({
        value: entityGroup.entityGroupId,
        label: (
          <Typography.Title className="permissions-scope-view-item-label" level={4}>
            {entityGroup.nameFullLocal}
          </Typography.Title>
        ),
        sortValue: entityGroup.nameFullLocal,
        data: [{ label: <FormattedMessage id="status" />, value: entityGroup.status }],
      });

      return acc;
    }, []);
  }

  if (userPermission.scope === PermissionScopeTypes.Entity) {
    return userPermission.scopeIds.reduce<ScopeOption[]>((acc, currentValue) => {
      if (!currentValue) {
        return acc;
      }

      const entity = entitiesData[currentValue];
      if (!entity) {
        return acc;
      }

      const entityGroup = entity.entityGroupId ? entityGroupsData[entity.entityGroupId] : null;

      acc.push({
        value: entity.entityId,
        label: (
          <div>
            <Typography.Title className="permissions-scope-view-item-label" level={4}>
              {entity?.nameFullLocal}
            </Typography.Title>
            <Typography.Text className="permissions-scope-view-item-label-subtitle">
              <FormattedMessage id="team.id" />
              {":  "}
              {entity.entityId}
            </Typography.Text>
            {entityGroup && (
              <Typography.Text className="permissions-scope-view-item-label-subtitle" style={{ display: "block", marginTop: "-4px" }}>
                <FormattedMessage id="club" />
                {":  "}
                {entityGroup.nameFullLocal}
              </Typography.Text>
            )}
          </div>
        ),
        sortValue: entity.nameFullLocal,
        data: [
          { label: <FormattedMessage id="gender" />, value: entity.gender },
          { label: <FormattedMessage id="ageGroup" />, value: entity.ageGroup },
          { label: <FormattedMessage id="discipline" />, value: entity.discipline },
          { label: <FormattedMessage id="status" />, value: entity.status },
        ],
      });
      return acc;
    }, []);
  }

  return [];
};

export default extractScopeData;
